import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Trash2, AlertTriangle, User as UserIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { AdminScript } from "@/hooks/useAdminContent";
import { cn } from "@/lib/utils";

interface ContentTableProps {
  scripts: AdminScript[];
  loading: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onView: (script: AdminScript) => void;
  onHide: (script: AdminScript) => void;
  onDelete: (script: AdminScript) => void;
}

export function ContentTable({
  scripts,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onView,
  onHide,
  onDelete,
}: ContentTableProps) {
  const { t, i18n } = useTranslation();
  const totalPages = Math.ceil(totalCount / pageSize);

  const getSceneTypeLabel = (sceneType: string) => {
    const labels: Record<string, string> = {
      recording: t('admin.content.sceneTypes.recording'),
      speech: t('admin.content.sceneTypes.speech'),
      meeting: t('admin.content.sceneTypes.meeting'),
      podcast: t('admin.content.sceneTypes.podcast'),
    };
    return labels[sceneType] || sceneType;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: t('admin.content.status.active'),
      hidden: t('admin.content.status.hidden'),
      flagged: t('admin.content.status.flagged'),
    };
    return labels[status] || status;
  };

  const SCENE_TYPE_CONFIG: Record<string, { color: string }> = {
    recording: { color: 'bg-[#FFD93D]' },
    speech: { color: 'bg-[#6FEDD6]' },
    meeting: { color: 'bg-[#FF6B9D]' },
    podcast: { color: 'bg-[#4DA6FF] text-white' },
  };

  const STATUS_CONFIG: Record<string, { color: string }> = {
    active: { color: 'bg-[#6FEDD6]' },
    hidden: { color: 'bg-gray-200 text-gray-500' },
    flagged: { color: 'bg-[#FF6B9D]' },
  };

  return (
    <div className="overflow-hidden rounded-2xl border-2 border-black bg-white shadow-[8px_8px_0_#1A1A1A]">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b-2 border-black bg-gray-50">
            <tr>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.title')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.user')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.sceneType')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.wordCount')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.status')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.content.columns.createdAt')}</th>
              <th className="px-6 py-4 font-black uppercase tracking-wider text-right">{t('admin.content.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-black/5">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-5 w-40 rounded bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-5 w-32 rounded bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-6 w-20 rounded-full bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-5 w-16 rounded bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-6 w-16 rounded-full bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-5 w-24 rounded bg-gray-200" /></td>
                  <td className="px-6 py-4"><div className="h-8 w-24 rounded bg-gray-200" /></td>
                </tr>
              ))
            ) : scripts.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500 font-bold">
                  {t('admin.content.noResults')}
                </td>
              </tr>
            ) : (
              scripts.map((script) => {
                const sceneConfig = SCENE_TYPE_CONFIG[script.scene_type] || { color: 'bg-gray-200' };
                const statusConfig = STATUS_CONFIG[script.status] || STATUS_CONFIG.active;

                return (
                  <tr key={script.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => onView(script)}
                        className="font-bold text-lg hover:text-[#4DA6FF] transition-colors text-left whitespace-nowrap"
                      >
                        {script.title}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                        <UserIcon className="h-4 w-4 shrink-0" />
                        <span>{script.user?.name || script.user?.email || t('admin.content.unknownUser')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        "inline-flex rounded-full border-2 border-black px-3 py-1 text-xs font-black whitespace-nowrap",
                        sceneConfig.color
                      )}>
                        {getSceneTypeLabel(script.scene_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-600 tabular-nums whitespace-nowrap">
                      {script.word_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        "inline-flex rounded-full border-2 border-black px-3 py-1 text-xs font-black whitespace-nowrap",
                        statusConfig.color
                      )}>
                        {getStatusLabel(script.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-500 text-sm whitespace-nowrap">
                      {new Date(script.created_at).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => onView(script)}
                          className="rounded-lg border-2 border-black p-2 bg-white hover:bg-[#6FEDD6] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
                          title={t('admin.content.actions.view')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onHide(script)}
                          className="rounded-lg border-2 border-black p-2 bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
                          title={script.status === 'hidden' ? t('admin.content.actions.restore') : t('admin.content.actions.hide')}
                        >
                          <EyeOff className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(script)}
                          className="rounded-lg border-2 border-black p-2 bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
                          title={t('admin.content.actions.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t-2 border-black p-4 bg-gray-50">
          <p className="text-sm font-medium text-gray-600">
            {t('admin.content.pagination.summary', { total: totalCount, current: page, pages: totalPages })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className={cn(
                "rounded-lg border-2 border-black p-2 font-black transition-all whitespace-nowrap",
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-[#6FEDD6] shadow-[2px_2px_0_#1A1A1A] hover:translate-x-0.5 hover:translate-y-0.5"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              className={cn(
                "rounded-lg border-2 border-black p-2 font-black transition-all whitespace-nowrap",
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-[#6FEDD6] shadow-[2px_2px_0_#1A1A1A] hover:translate-x-0.5 hover:translate-y-0.5"
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
